const designElements = [
  { label: "Material", detail: "Linen, cotton-linen blend, boucle, or velvet — natural fibres that add texture and age beautifully. Avoid synthetic covers that look flat and feel cheap." },
  { label: "Tone", detail: "Stay within a warm neutral family: ivory, oat, sand, warm white, and soft camel. Avoid cool greys or stark whites that break the warmth of the room." },
  { label: "Texture", detail: "Mix textures within the same palette — a smooth linen next to a boucle next to a woven cotton creates depth without introducing colour." },
  { label: "Size", detail: "A mix of sizes reads as more considered: 60x60cm as the main pillow, 45x45cm as a secondary, and a lumbar (30x50cm) for the front layer." },
  { label: "Fill", detail: "Feather-down inserts give the best shape and that slightly overstuffed, luxurious look. Foam inserts tend to look stiff and boxy." },
  { label: "Quantity", detail: "For a two-seater sofa: 4–5 pillows. For a three-seater: 5–7. Odd numbers almost always look more natural than even." },
];

const stylingTips = [
  "Work in threes: two large pillows at the back, one medium in front, and a lumbar at the centre. This is the most reliable arrangement for a calm, editorial look.",
  "Stay within a three-tone palette — for example, ivory, warm oat, and soft sand. More than three tones starts to feel busy.",
  "Mix at least two different textures: a smooth linen with a boucle, or a woven cotton with a velvet. Same tone, different texture is the key to depth.",
  "Vary the sizes deliberately — the largest pillows go at the back, the smallest or lumbar at the front. This creates a natural layered effect.",
  "Chop the pillows: fold them in half and press down the centre to create that relaxed, slightly slouched look. It reads as effortless rather than staged.",
  "Avoid matching sets — pillows that are too coordinated look like a hotel room. Mix pieces from different sources within the same palette.",
  "Rotate your pillows seasonally: heavier boucle and velvet in autumn and winter, lighter linen and cotton in spring and summer.",
];

export default function TPContent() {
  return (
    <article className="max-w-2xl mx-auto px-4 md:px-0 py-16">

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Introduction</p>
        <p className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Throw pillows are one of the easiest and most impactful ways to change how a room feels.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          The right combination of beige throw pillows can take a sofa from flat and unfinished to warm, layered, and genuinely inviting.
          The key is not finding the perfect single pillow — it is building a small collection of pieces that work together in tone and texture.
          This guide covers what to look for, how to arrange them, and which picks we keep returning to.
        </p>
      </div>

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Why It Works</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Why Texture Matters More Than Colour
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/78db0db09e204a97cf34cbdd2bc3bf69.jpg"
            alt="Mixed texture beige throw pillows"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          In a calm neutral room, colour contrast is not the goal — textural contrast is. When you mix a smooth linen pillow with a boucle and a woven cotton,
          all in the same warm beige palette, the result is a sofa that looks rich and considered without introducing any visual noise.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          Each material catches light differently. Linen has a soft matte quality. Boucle has a looped, almost sculptural texture.
          Velvet shifts from light to dark as you move around it. Together, they create a depth that a single material — however beautiful — simply cannot achieve.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          The palette stays calm and unified. The texture does all the work. This is the principle behind every beautifully styled sofa you have ever admired.
        </p>
      </div>

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>What to Look For</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Key Things to Consider When Choosing Throw Pillows
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <div className="space-y-4">
          {designElements.map((el, i) => (
            <div key={i} className="flex gap-5 items-start border-b border-[#EDE6DA] pb-4 last:border-0">
              <span className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase w-24 flex-shrink-0 pt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>{el.label}</span>
              <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{el.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Styling</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          How to Arrange Throw Pillows on a Sofa
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://readdy.ai/api/search-image?query=beige%20sofa%20beautifully%20arranged%20throw%20pillows%20layered%20linen%20boucle%20lumbar%20cushion%20warm%20afternoon%20light%20calm%20living%20room%20editorial%20interior%20photography%20neutral%20palette&width=900&height=500&seq=tp_style1&orientation=landscape"
            alt="Beautifully arranged throw pillows on a beige sofa"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <ul className="space-y-4">
          {stylingTips.map((tip, i) => (
            <li key={i} className="flex gap-4 items-start">
              <span className="text-[#C8BAA8] text-[10px] tracking-[0.3em] mt-1 flex-shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>0{i + 1}</span>
              <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{tip}</p>
            </li>
          ))}
        </ul>
      </div>

    </article>
  );
}
